import { messageWithOrWithoutCount, SiteUrlsContext } from '@tableau/data-catalog-shared';
import { stringListToKeySet } from '@tableau/data-structures';
import { SiteUrls } from '@tableau/shared-urls';
import * as React from 'react';
import { Redirect, Route } from 'react-router';
import { RouteComponentProps, Switch } from 'react-router-dom';
import { Messages } from '../../../gen-src/Localize';
import { LensQuery_lenses } from '../../../gen-src/queries/LensQuery';
import { BrowserTitleUpdater, BrowserTitleUpdateType } from '../BrowserTitleUpdater';
import { ErrorListProvider } from '../error/components/ErrorListProvider';
import { ErrorToasts } from '../error/components/ErrorToasts';
import { UnrecognizedUrl } from '../error/components/UnrecognizedUrl';
import { getAnchorCountFromConfig, LineageItemConfig, lineageItemKey, LineageSection, LineageSectionType } from '../placePage/LineageConfiguration';
import { LensLineageSubplaceLayout } from '../placePage/LineageSubplaceLayout';
import { LensFieldDetailsDialog } from './LensFieldDetailsDialog';
import { LensLineageItemConfig, LensLineagePaneParams, LensLineageSection, memoizedGetLensLineageConfiguration } from './LensLineageConfiguration';
import { LensLineageCounts, LensLineageCountsQueryWrapper } from './LensLineageCountsQueryWrapper';
import { LensQueryWrapper } from './LensQueryWrapper';
import { LensSubplaceParamsProvider } from './LensSubplaceParamsProvider';

export type LensLineageSubplaceRouterProps = {
  readonly vizportalUrlId: string;
};

export const LensLineageSubplaceRouter: React.FC<LensLineageSubplaceRouterProps> = props => {

  return <SiteUrlsContext.Consumer>
    {urls =>
      <Route
        path={urls.lensLineagePath(props.vizportalUrlId)}
        render={() => renderLensLineageRoute(urls, props.vizportalUrlId)} />
    }
  </SiteUrlsContext.Consumer>
};

const renderLensLineageRoute = (urls: SiteUrls, vizportalUrlId: string): JSX.Element => {
  return (
    <Switch>
      {memoizedGetLensLineageConfiguration().map(lineageSection =>
        lineageSection.items.map((lineageItem, i) =>
          <Route
            key={i}
            path={lineageItem.path(urls, vizportalUrlId)}
            render={() => renderLineageItemRoute(vizportalUrlId, urls, lineageItem, lineageSection)}
          />
        )
      )}
      <Redirect
        exact={true}
        from={urls.lensLineagePath(vizportalUrlId)}
        to={urls.lensLineageFieldsPath(vizportalUrlId)}
      />
      <UnrecognizedUrl
        backToButtonLabel={Messages.BackToFields()}
        backToButtonUrl={urls.lensLineageFieldsPath(vizportalUrlId)}
      />
    </Switch>
  );
};

const renderLineageItemRoute = (
  vizportalUrlId: string,
  urls: SiteUrls,
  lineageItem: LensLineageItemConfig,
  lineageSection: LensLineageSection): JSX.Element => {
  return (
    <>
      <LensSubplaceParamsProvider>
        {_ =>
          <ErrorListProvider>
            <ErrorToasts />
            <LensQueryWrapper
              vizportalUrlId={vizportalUrlId}>
              {lens => {
                return <LensLineageCountsQueryWrapper
                  vizportalUrlId={vizportalUrlId}>
                  {({ countsLoading, unfilteredCounts }) =>
                    <>
                      <LensLineageSubplace
                        vizportalUrlId={vizportalUrlId}
                        lens={lens}
                        lineageItem={lineageItem}
                        lineageSection={lineageSection}
                        countsLoading={countsLoading}
                        unfilteredCounts={unfilteredCounts} />
                      {lineageSection.sectionType === LineageSectionType.Anchor ?
                        <Route
                          path={urls.lensLineageFieldPath(vizportalUrlId, ':fieldId')}
                          render={({ history, location, match }: RouteComponentProps<{ fieldId: string; }>) =>
                            <LensFieldDetailsDialog
                              fieldId={match.params.fieldId}
                              makeFieldUrl={(fieldId) => urls.lensLineageFieldUrl(vizportalUrlId, fieldId)}
                              onClose={() => history.replace({
                                ...location,
                                pathname: urls.lensLineageFieldsPath(vizportalUrlId)
                              })}
                              lensLuid={lens.luid}
                            />
                          } /> : null}</>}
                </LensLineageCountsQueryWrapper>
              }}
            </LensQueryWrapper>
          </ErrorListProvider>
        }
      </LensSubplaceParamsProvider>
    </>
  );
};

type LensLineageSubplaceProps = Readonly<{
  vizportalUrlId: string;
  lens: LensQuery_lenses;
  lineageItem: LineageItemConfig<LensLineageCounts, LensLineagePaneParams>;
  lineageSection: LineageSection<LensLineageCounts, LensLineagePaneParams>;
  countsLoading: boolean;
  unfilteredCounts: LensLineageCounts | null;
}>;

const LensLineageSubplace: React.FC<LensLineageSubplaceProps> = props => {
  return <>
    <BrowserTitleUpdater
      name={"Aloha world"}
      browserTitleUpdateType={BrowserTitleUpdateType.TITLE_WITH_ENTITY_NAME_AND_SUBSPACE_NAME}
      subspaceName={Messages.LineageNavHeader()}
    />
    <LensLineageSubplaceLayout
      pane={props.lineageItem.paneFactory({
        title: messageWithOrWithoutCount(
          props.unfilteredCounts && props.lineageItem.count(props.unfilteredCounts),
          props.lineageItem.messagesForPane,
        ),
        vizportalUrlId: props.vizportalUrlId,
        count: props.unfilteredCounts && props.lineageItem.count(props.unfilteredCounts),
        selectedKeys: stringListToKeySet([]),
        updateSelectionForKey: () => { return; }
      })}
      activeLineageItemKey={lineageItemKey(props)}
      filteredOnItemType={null}
      selectedAnchorItemIds={[]}
      urlSearchValue={''}
      anchorName={props.lens.name}
      anchorCount={getAnchorCountFromConfig(
        memoizedGetLensLineageConfiguration(),
        props.unfilteredCounts)}
      countsLoading={props.countsLoading}
      unfilteredCounts={props.unfilteredCounts}
      filteredCounts={null}
      filteredItemName={null}
      vizportalUrlId={props.vizportalUrlId} />
  </>;
}

